// Email Signature Image API - Upload images to GCS for email signatures
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';

/**
 * Factory function to create signature image API service with ApiClient dependency injection
 * @param apiClient - ApiClient instance from context
 * @returns Service object with signature image upload methods
 */
export const createSignatureImageApi = (apiClient: ApiClient) => ({
  /**
   * Upload signature image to Google Cloud Storage
   * POST /communications/signature_images
   *
   * Validates:
   * - File size must be less than 2MB
   * - File must be an image type
   *
   * @param file - Image file to upload
   * @returns GCS public URL for the uploaded image
   * @throws Error if file is too large or not an image
   */
  async uploadImage(file: File): Promise<string> {
    // Validate file size (2MB limit as per backend)
    const MAX_SIZE = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > MAX_SIZE) {
      throw new Error('Image must be less than 2MB');
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Prepare FormData for multipart upload
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{
      data: {
        file_url: string; // Public GCS URL
        file_type: string; // MIME type
      };
    }>(
      '/communications/signature_images',
      formData,
      // Important: Set Content-Type for multipart/form-data
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data.file_url;
  },
});

// Type for the service instance
export type SignatureImageApi = ReturnType<typeof createSignatureImageApi>;
